/**
 * Dealer inventory filter/sort params (Dealer Profile M8).
 */
import { VEHICLE_SEARCH_SORT } from "./vehicleSearchSortConstants";
import {
  clampPage,
  clampPageSize,
  normalizeBodyTypeFilter,
  normalizeCanonicalSort,
  normalizeTransmissionFilter,
  parseCsvParam,
} from "./vehicleSearchParamsConstants";

export const DEALER_INVENTORY_DEFAULT_PAGE_SIZE = 24;

export const DEALER_INVENTORY_SORT_OPTIONS = [
  { value: VEHICLE_SEARCH_SORT.RECOMMENDED, label: "Recomendados" },
  { value: VEHICLE_SEARCH_SORT.NEWEST, label: "Más recientes" },
  { value: VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED, label: "Actualizados recientemente" },
  { value: VEHICLE_SEARCH_SORT.LOWEST_PRICE, label: "Menor precio" },
  { value: VEHICLE_SEARCH_SORT.HIGHEST_PRICE, label: "Mayor precio" },
  { value: VEHICLE_SEARCH_SORT.LOWEST_MILEAGE, label: "Menor kilometraje" },
  { value: VEHICLE_SEARCH_SORT.NEWEST_YEAR, label: "Año más reciente" },
  { value: VEHICLE_SEARCH_SORT.BEST_VALUE, label: "Mejor oportunidad" },
];

export const DEALER_INVENTORY_FILTER_COPY_ES = {
  filterInventory: "Filtrar inventario",
  filter: "Filtrar",
  sort: "Ordenar",
  close: "Cerrar",
  applyFilters: "Aplicar filtros",
  appliedFilters: "Filtros aplicados",
  clearFilters: "Limpiar filtros",
  showResults: "Mostrar resultados",
  viewAll: "Ver todos los vehículos",
  filteredEmpty:
    "No encontramos vehículos con estos filtros en este concesionario.",
  filteredCountOne: "1 vehículo con estos filtros",
  filteredCountMany: (count) => `${count} vehículos con estos filtros`,
  searchMore: "Buscar más carros usados",
};

/** URL keys owned by dealer inventory filters (UTM/other params preserved). */
export const DEALER_INVENTORY_URL_KEYS = [
  "make",
  "model",
  "yearMin",
  "yearMax",
  "priceMinCop",
  "priceMaxCop",
  "mileageMaxKm",
  "bodyType",
  "transmission",
  "fuelType",
  "fairPriceLabels",
  "riskLevels",
  "hasPhotos",
  "sort",
  "page",
  "pageSize",
];

/**
 * @typedef {Object} DealerInventorySearchParams
 * @property {string|null} make
 * @property {string|null} model
 * @property {number|null} yearMin
 * @property {number|null} yearMax
 * @property {number|null} priceMinCop
 * @property {number|null} priceMaxCop
 * @property {number|null} mileageMaxKm
 * @property {string|null} bodyType
 * @property {string|null} transmission
 * @property {string|null} fuelType
 * @property {string[]} fairPriceLabels
 * @property {string[]} riskLevels
 * @property {boolean} hasPhotos
 * @property {string} sort
 * @property {number} page
 * @property {number} pageSize
 */

export function createDefaultDealerInventorySearchParams() {
  return {
    make: null,
    model: null,
    yearMin: null,
    yearMax: null,
    priceMinCop: null,
    priceMaxCop: null,
    mileageMaxKm: null,
    bodyType: null,
    transmission: null,
    fuelType: null,
    fairPriceLabels: [],
    riskLevels: [],
    hasPhotos: false,
    sort: VEHICLE_SEARCH_SORT.RECENTLY_CONFIRMED,
    page: 1,
    pageSize: DEALER_INVENTORY_DEFAULT_PAGE_SIZE,
  };
}

function pickNumber(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @param {URLSearchParams|string|object} input
 * @returns {DealerInventorySearchParams}
 */
export function parseDealerInventorySearchParams(input) {
  const defaults = createDefaultDealerInventorySearchParams();

  if (input && typeof input === "object" && !(input instanceof URLSearchParams)) {
    const raw = input;
    return {
      make: raw.make?.trim?.() || raw.make || null,
      model: raw.model?.trim?.() || raw.model || null,
      yearMin: pickNumber(raw.yearMin),
      yearMax: pickNumber(raw.yearMax),
      priceMinCop: pickNumber(raw.priceMinCop),
      priceMaxCop: pickNumber(raw.priceMaxCop),
      mileageMaxKm: pickNumber(raw.mileageMaxKm),
      bodyType: raw.bodyType ? normalizeBodyTypeFilter(raw.bodyType) : null,
      transmission: raw.transmission ? normalizeTransmissionFilter(raw.transmission) : null,
      fuelType: raw.fuelType?.trim?.()?.toLowerCase() || raw.fuelType || null,
      fairPriceLabels: Array.isArray(raw.fairPriceLabels)
        ? raw.fairPriceLabels
        : parseCsvParam(raw.fairPriceLabels),
      riskLevels: Array.isArray(raw.riskLevels) ? raw.riskLevels : parseCsvParam(raw.riskLevels),
      hasPhotos: raw.hasPhotos === true,
      sort: raw.sort ? normalizeCanonicalSort(raw.sort) : defaults.sort,
      page: clampPage(raw.page),
      pageSize: clampPageSize(raw.pageSize, DEALER_INVENTORY_DEFAULT_PAGE_SIZE),
    };
  }

  const sp =
    input instanceof URLSearchParams
      ? input
      : new URLSearchParams(typeof input === "string" ? input.replace(/^\?/, "") : "");

  const sortRaw = sp.get("sort");
  const sort = sortRaw ? normalizeCanonicalSort(sortRaw) : defaults.sort;

  return {
    make: sp.get("make")?.trim() || null,
    model: sp.get("model")?.trim() || null,
    yearMin: pickNumber(sp.get("yearMin") || sp.get("year_min")),
    yearMax: pickNumber(sp.get("yearMax") || sp.get("year_max")),
    priceMinCop: pickNumber(sp.get("priceMinCop") || sp.get("price_min")),
    priceMaxCop: pickNumber(sp.get("priceMaxCop") || sp.get("price_max")),
    mileageMaxKm: pickNumber(sp.get("mileageMaxKm") || sp.get("mileage_max")),
    bodyType: normalizeBodyTypeFilter(sp.get("bodyType") || sp.get("body_type")),
    transmission: normalizeTransmissionFilter(sp.get("transmission")),
    fuelType: sp.get("fuelType")?.trim()?.toLowerCase() || sp.get("fuel_type")?.trim()?.toLowerCase() || null,
    fairPriceLabels: parseCsvParam(sp.get("fairPriceLabels") || sp.get("fair_price_labels")),
    riskLevels: parseCsvParam(sp.get("riskLevels") || sp.get("risk_levels")),
    hasPhotos: sp.get("hasPhotos") === "true" || sp.get("has_photos") === "true",
    sort,
    page: clampPage(sp.get("page")),
    pageSize: clampPageSize(sp.get("pageSize"), DEALER_INVENTORY_DEFAULT_PAGE_SIZE),
  };
}

/**
 * @param {DealerInventorySearchParams|object} params
 * @returns {URLSearchParams}
 */
export function serializeDealerInventorySearchParams(params) {
  const sp = new URLSearchParams();
  const p = parseDealerInventorySearchParams(params);
  const defaults = createDefaultDealerInventorySearchParams();

  if (p.make) sp.set("make", p.make);
  if (p.model) sp.set("model", p.model);
  if (p.yearMin != null) sp.set("yearMin", String(p.yearMin));
  if (p.yearMax != null) sp.set("yearMax", String(p.yearMax));
  if (p.priceMinCop != null) sp.set("priceMinCop", String(p.priceMinCop));
  if (p.priceMaxCop != null) sp.set("priceMaxCop", String(p.priceMaxCop));
  if (p.mileageMaxKm != null) sp.set("mileageMaxKm", String(p.mileageMaxKm));
  if (p.bodyType) sp.set("bodyType", p.bodyType);
  if (p.transmission) sp.set("transmission", p.transmission);
  if (p.fuelType) sp.set("fuelType", p.fuelType);
  if (p.fairPriceLabels?.length) sp.set("fairPriceLabels", p.fairPriceLabels.join(","));
  if (p.riskLevels?.length) sp.set("riskLevels", p.riskLevels.join(","));
  if (p.hasPhotos) sp.set("hasPhotos", "true");
  if (p.sort && p.sort !== defaults.sort) sp.set("sort", p.sort);
  if (p.page > 1) sp.set("page", String(p.page));
  if (p.pageSize !== defaults.pageSize) sp.set("pageSize", String(p.pageSize));

  return sp;
}

export function hasActiveDealerInventoryFilters(params) {
  const p = parseDealerInventorySearchParams(params);
  const defaults = createDefaultDealerInventorySearchParams();
  return (
    Boolean(p.make) ||
    Boolean(p.model) ||
    p.yearMin != null ||
    p.yearMax != null ||
    p.priceMinCop != null ||
    p.priceMaxCop != null ||
    p.mileageMaxKm != null ||
    Boolean(p.bodyType) ||
    Boolean(p.transmission) ||
    Boolean(p.fuelType) ||
    (p.fairPriceLabels?.length ?? 0) > 0 ||
    (p.riskLevels?.length ?? 0) > 0 ||
    p.hasPhotos ||
    p.sort !== defaults.sort
  );
}

/**
 * Merge dealer inventory params into existing URLSearchParams, preserving unrelated keys (e.g. UTM).
 * @param {URLSearchParams} current
 * @param {DealerInventorySearchParams|object} nextParams
 */
export function mergeDealerInventorySearchParams(current, nextParams) {
  const sp = new URLSearchParams(current);
  DEALER_INVENTORY_URL_KEYS.forEach((key) => sp.delete(key));
  const serialized = serializeDealerInventorySearchParams(nextParams);
  serialized.forEach((value, key) => sp.set(key, value));
  return sp;
}

export function clearDealerInventorySearchParams(current = {}) {
  return createDefaultDealerInventorySearchParams();
}
